import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CAMPAIGN } from "@/constants/testIds";
import { SERVICES, PRIORITIES, CHANNELS, MEMBERSHIP, SG_OPS, track } from "@/constants/campaignDefs";
import { AlertCircle, Loader2 } from "lucide-react";

const LIME = "#B7E64B";

function Legend({ children, required }) {
  return (
    <legend className="mb-2 block text-sm font-semibold text-[#09292D]">
      {children} {required && <span className="text-rose-600">*</span>}
    </legend>
  );
}

function FieldError({ msg, id }) {
  if (!msg) return null;
  return <p id={id} className="mt-1 flex items-center gap-1 text-xs font-medium text-rose-600"><AlertCircle className="h-3.5 w-3.5" /> {msg}</p>;
}

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-[#09292D] placeholder:text-slate-400 focus:border-[#09292D] focus:outline-none focus:ring-2 focus:ring-[#B7E64B]";

export default function CampaignForm({ config }) {
  const navigate = useNavigate();
  const summaryRef = useRef(null);
  const started = useRef(false);
  const markStart = () => { if (!started.current) { started.current = true; track("form_start"); } };
  const [f, setF] = useState({
    name: "", company: "", email: "", contact_channel: "", phone: "",
    lias_membership: "", sg_operations: "",
  });
  const [services, setServices] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [marketing, setMarketing] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const state = config?.state || "open";
  const waitlistMode = state === "full";
  const set = (k, v) => { markStart(); setF((p) => ({ ...p, [k]: v })); };

  const toggleService = (k) =>
    setServices((p) => { markStart(); return p.includes(k) ? p.filter((x) => x !== k) : [...p, k]; });

  const togglePriority = (k) => {
    markStart();
    setPriorities((p) => {
      if (k === "not_sure") return p.includes("not_sure") ? [] : ["not_sure"];
      let next = p.filter((x) => x !== "not_sure");
      if (next.includes(k)) next = next.filter((x) => x !== k);
      else if (next.length < 3) next = [...next, k];
      return next;
    });
  };

  const validate = () => {
    const e = {};
    if (!f.name.trim()) e.name = "Please tell us your name.";
    if (!f.company.trim()) e.company = "Please enter your company name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) e.email = "Please enter a valid email address.";
    if (!f.contact_channel) e.contact_channel = "Please choose how we should contact you.";
    if ((f.contact_channel === "phone" || f.contact_channel === "whatsapp")) {
      if (f.phone.replace(/\D/g, "").length < 8) e.phone = "Please enter a valid phone number for your chosen channel.";
    }
    if (!f.lias_membership) e.lias_membership = "Please tell us your LIAS membership status.";
    if (!f.sg_operations) e.sg_operations = "Please tell us about your Singapore operations.";
    if (services.length === 0) e.services = "Please select at least one service.";
    if (priorities.length === 0) e.priorities = "Please choose what would help most (up to three).";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      setTimeout(() => summaryRef.current?.focus(), 0);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...f, services, priorities, marketing_opt_in: marketing,
        notice_version: config?.notice_version,
      };
      const { data } = await api.post("/campaign/register", payload);
      if (!data.duplicate) track("register_success");
      navigate("/liasroots/confirmation", { state: data });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 422 && detail?.field_errors) {
        setErrors(detail.field_errors);
        setTimeout(() => summaryRef.current?.focus(), 0);
      } else if (err?.response?.status === 409) {
        navigate("/liasroots/confirmation", { state: { closed: true } });
      } else {
        setErrors({ _form: typeof detail === "string" ? detail : "Something went wrong. Please try again." });
        setTimeout(() => summaryRef.current?.focus(), 0);
      }
    }
    setSubmitting(false);
  };

  if (state === "closed") {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid={CAMPAIGN.form}>
        <h3 className="font-heading text-xl font-bold text-[#09292D]">Registration is closed</h3>
        <p className="mt-2 text-sm text-slate-600">Thank you for your interest. Registration for the LIASROOTS pilot is currently closed. Please check back for future opportunities.</p>
      </div>
    );
  }

  const errorList = Object.entries(errors).filter(([k]) => k !== "_form");

  return (
    <form onSubmit={submit} noValidate className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm" data-testid={CAMPAIGN.form}>
      <h3 className="font-heading text-2xl font-bold text-[#09292D]">Tell us about your landscaping business</h3>
      <p className="mt-1 text-sm text-slate-600">A few quick questions. No account, documents or payment needed.</p>

      {(errorList.length > 0 || errors._form) && (
        <div ref={summaryRef} tabIndex={-1} role="alert" data-testid={CAMPAIGN.errorSummary}
             className="mt-4 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 focus:outline-none focus:ring-2 focus:ring-rose-400">
          <p className="font-semibold">Please fix the following:</p>
          {errors._form && <p className="mt-1">{errors._form}</p>}
          <ul className="mt-1 list-disc pl-5">
            {errorList.map(([k, v]) => <li key={k}>{v}</li>)}
          </ul>
        </div>
      )}

      {waitlistMode && (
        <div className="mt-4 rounded-lg border-l-4 border-[#B7E64B] bg-[#f3f8e6] p-3 text-sm text-[#09292D]">
          The current cohort is full. You can still register to join the <b>waitlist</b> — we'll be in touch if a place opens.
        </div>
      )}

      <div className="mt-6 space-y-6">
        {/* Contact first */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="c-name" className="mb-1 block text-sm font-semibold text-[#09292D]">Your name <span className="text-rose-600">*</span></label>
            <input id="c-name" className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)}
                   aria-invalid={!!errors.name} data-testid={CAMPAIGN.field("name")} />
            <FieldError msg={errors.name} id="err-name" />
          </div>
          <div>
            <label htmlFor="c-company" className="mb-1 block text-sm font-semibold text-[#09292D]">Company name <span className="text-rose-600">*</span></label>
            <input id="c-company" className={inputCls} value={f.company} onChange={(e) => set("company", e.target.value)}
                   aria-invalid={!!errors.company} data-testid={CAMPAIGN.field("company")} />
            <FieldError msg={errors.company} id="err-company" />
          </div>
        </div>

        <div>
          <label htmlFor="c-email" className="mb-1 block text-sm font-semibold text-[#09292D]">Follow-up email <span className="text-rose-600">*</span></label>
          <input id="c-email" type="email" className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)}
                 aria-invalid={!!errors.email} data-testid={CAMPAIGN.field("email")} placeholder="you@company.com.sg" />
          <FieldError msg={errors.email} id="err-email" />
        </div>

        <fieldset>
          <Legend required>Preferred contact channel</Legend>
          <div className="flex flex-wrap gap-2">
            {CHANNELS.map((c) => (
              <button type="button" key={c.key} onClick={() => set("contact_channel", c.key)}
                      data-testid={CAMPAIGN.channel(c.key)}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${f.contact_channel === c.key ? "border-[#09292D] bg-[#09292D] text-white" : "border-slate-300 bg-white text-[#09292D] hover:border-[#09292D]"}`}>
                {c.label}
              </button>
            ))}
          </div>
          <FieldError msg={errors.contact_channel} id="err-channel" />
          {(f.contact_channel === "phone" || f.contact_channel === "whatsapp") && (
            <div className="mt-3">
              <label htmlFor="c-phone" className="mb-1 block text-sm font-semibold text-[#09292D]">Phone number <span className="text-rose-600">*</span></label>
              <input id="c-phone" className={inputCls} value={f.phone} onChange={(e) => set("phone", e.target.value)}
                     aria-invalid={!!errors.phone} data-testid={CAMPAIGN.phone} placeholder="+65 ..." />
              <FieldError msg={errors.phone} id="err-phone" />
            </div>
          )}
        </fieldset>

        {/* Landscaping context */}
        <fieldset>
          <Legend required>Are you a LIAS member?</Legend>
          <div className="flex flex-wrap gap-2">
            {MEMBERSHIP.map((m) => (
              <button type="button" key={m.key} onClick={() => set("lias_membership", m.key)}
                      data-testid={CAMPAIGN.membership(m.key)}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${f.lias_membership === m.key ? "border-[#09292D] bg-[#09292D] text-white" : "border-slate-300 bg-white text-[#09292D] hover:border-[#09292D]"}`}>
                {m.label}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-slate-500">No membership number needed.</p>
          <FieldError msg={errors.lias_membership} id="err-membership" />
        </fieldset>

        <fieldset>
          <Legend required>Do you operate in Singapore?</Legend>
          <div className="flex flex-wrap gap-2">
            {SG_OPS.map((m) => (
              <button type="button" key={m.key} onClick={() => set("sg_operations", m.key)}
                      data-testid={CAMPAIGN.sgops(m.key)}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${f.sg_operations === m.key ? "border-[#09292D] bg-[#09292D] text-white" : "border-slate-300 bg-white text-[#09292D] hover:border-[#09292D]"}`}>
                {m.label}
              </button>
            ))}
          </div>
          <FieldError msg={errors.sg_operations} id="err-sg" />
        </fieldset>

        <fieldset>
          <Legend required>Services you offer</Legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {SERVICES.map((s) => (
              <label key={s.key} data-testid={CAMPAIGN.service(s.key)}
                     className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${services.includes(s.key) ? "border-[#09292D] bg-[#f3f8e6]" : "border-slate-200 bg-white"}`}>
                <input type="checkbox" checked={services.includes(s.key)} onChange={() => toggleService(s.key)} className="h-4 w-4 accent-[#09292D]" />
                <span className="text-[#09292D]">{s.label}</span>
              </label>
            ))}
          </div>
          <FieldError msg={errors.services} id="err-services" />
        </fieldset>

        <fieldset>
          <Legend required>What would help most? <span className="font-normal text-slate-500">(up to three)</span></Legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {PRIORITIES.map((p) => {
              const checked = priorities.includes(p.key);
              const disabled = !checked && p.key !== "not_sure" && priorities.length >= 3 && !priorities.includes("not_sure");
              return (
                <label key={p.key} data-testid={CAMPAIGN.priority(p.key)}
                       className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${checked ? "border-[#09292D] bg-[#f3f8e6]" : "border-slate-200 bg-white"} ${disabled ? "opacity-40" : ""}`}>
                  <input type="checkbox" checked={checked} disabled={disabled} onChange={() => togglePriority(p.key)} className="h-4 w-4 accent-[#09292D]" />
                  <span className="text-[#09292D]">{p.label}</span>
                </label>
              );
            })}
          </div>
          <FieldError msg={errors.priorities} id="err-priorities" />
        </fieldset>

        {/* Privacy + marketing */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-600">
          {config?.notice_text}
        </div>
        <label className="flex items-start gap-2.5 text-sm text-[#09292D]" data-testid={CAMPAIGN.marketing}>
          <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#09292D]" />
          <span>Send me occasional LIASROOTS updates by email (optional — you can unsubscribe anytime). Your enquiry works without this.</span>
        </label>

        <button type="submit" disabled={submitting} data-testid={CAMPAIGN.submit}
                className="flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-base font-bold text-[#09292D] transition-transform hover:scale-[1.01] disabled:opacity-60"
                style={{ backgroundColor: LIME }}>
          {submitting && <Loader2 className="h-5 w-5 animate-spin" />}
          {waitlistMode ? "Join the pilot waitlist" : "Register my interest"}
        </button>
        <p className="text-center text-xs text-slate-500">Registering is not a confirmed place. Selection depends on eligibility, suitability and a written scope.</p>
      </div>
    </form>
  );
}
